import { datadogRum } from '@datadog/browser-rum';
import type { ErrorResponse } from '@apollo/client/link/error';
import type { EnhancedVideoJsPlayer } from 'video.js';
import type Tech from 'video.js/dist/types/tech/tech';
import type VideoJsMediaError from 'video.js/dist/types/media-error';
import getConfigVars from '#utils/get-config-vars';
import { probeHlsSource } from '#modules/player/util/probeHlsSource';
import { PlayerErrorCode } from '#modules/player/plugins/errors/PlayerError';

const APPLICATION_ID = '12a49534-a267-4754-a3a9-cddc2daad351';
const CLIENT_TOKEN = 'pubd8319b9edcd971cb794655f7282bb00c';

// This variable is used to check if DD initilized or not
let ddInitialized = false;

export const _getSessionSampleRate = () => 100;

export const _getSessionReplaySampleRate = (env: string) => {
  if (env === 'prod') return 5;
  return 20;
};

export const _getPrivacyLevel = (env: string) => {
  if (env === 'prod') return 'mask';
  return 'allow';
};

const isDDEnabled = (env: string) => ['dev', 'stage', 'prod'].includes(env);

const _getAllowedUrls = (env: string): string[] => {
  if (env === 'dev')
    return [
      'https://veejoy.raccoon-dev.europapark.systems',
      'https://raccoon-dev.europapark.systems',
    ];
  if (env === 'stage')
    return [
      'https://stage.veejoy.de',
      'https://raccoon-stage.europapark.systems',
    ];
  if (env === 'prod') return ['https://www.veejoy.de'];
  return [];
};

export const initDD = () => {
  const { ENV } = getConfigVars();

  if (isDDEnabled(ENV)) {
    const { VERSION } = getConfigVars();
    datadogRum.init({
      clientToken: CLIENT_TOKEN,
      applicationId: APPLICATION_ID,
      site: 'datadoghq.eu',
      service: 'veejoy.frontend',
      env: ENV,
      version: VERSION,
      sessionSampleRate: _getSessionSampleRate(),
      sessionReplaySampleRate: _getSessionReplaySampleRate(ENV),
      defaultPrivacyLevel: _getPrivacyLevel(ENV),
      allowedTracingUrls: _getAllowedUrls(ENV),
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
    });
    ddInitialized = true;
  }
};

export const isDDInitialized = () => ddInitialized;

// The OneTrust consent banner is only loaded on prod.
export const isConsentRequired = () => getConfigVars().ENV === 'prod';

const namedError = (name: string, message: string) => {
  const error = new Error(message);
  error.name = name;
  return error;
};

const LOG_TAIL_LENGTH = 40;
const LOG_WARNINGS_LENGTH = 10;
const MAX_TEXT_LENGTH = 300;

const mediaContext = (player: EnhancedVideoJsPlayer) => ({
  mediaIri: player.mediaData?.iri,
  contentType: player.mediaData?.contentType,
  title: player.title,
  isLivestreaming: player.isLivestreaming,
  isDrmProtected: player.mediaPlayerData?.source?.isDrmProtected,
  // What is left after dropping the tracks this browser can't play.
  playableTrackCount: player.mediaSource?.tracks.length,
  language: player.mediaSource?.language,
});

const formatLogArg = (arg: unknown) => {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  // Objects are left out: some player log lines carry the DRM license headers.
  if (typeof arg === 'object' && arg !== null) return '[object]';
  return String(arg);
};

const formatLogEntries = (entries: unknown[][]) =>
  entries.map((args) =>
    args.map(formatLogArg).join(' ').slice(0, MAX_TEXT_LENGTH)
  );

// video.js keeps every log line in a shared history, whatever the log level. An entry is
// [loggerName, 'WARN:' | 'ERROR:' | 'DEBUG:', ...args], or without the level for plain logs.
const logContext = (player: EnhancedVideoJsPlayer) => {
  const history = player.log.history() as unknown[][];
  return {
    logTail: formatLogEntries(history.slice(-LOG_TAIL_LENGTH)),
    logWarnings: formatLogEntries(
      history
        .filter(([, level]) => level === 'WARN:' || level === 'ERROR:')
        .slice(-LOG_WARNINGS_LENGTH)
    ),
  };
};

const playbackContext = (player: EnhancedVideoJsPlayer) => {
  const tech = player.tech_ as (Tech & { vhs?: unknown }) | undefined;
  return {
    src: player.currentSrc(),
    // False when the error was raised before this media's source was set, or during an
    // ad: the playback fields then describe something else.
    srcIsCurrentMedia: player.currentSrc() === player.mediaSource?.url,
    type: player.currentType(),
    usesVhs: Boolean(tech?.vhs),
    currentTime: player.currentTime(),
    // video.js keeps only the code of a native error and drops the browser's own message.
    nativeMessage: (tech?.el() as HTMLMediaElement | undefined)?.error?.message,
    ...logContext(player),
  };
};

// Fields VHS and videojs-contrib-eme add to the errors they hand to the player.
type StreamingMediaError = VideoJsMediaError & {
  responseText?: unknown; // HLS playlist errors
  response?: unknown; // DASH manifest errors
  cause?: unknown; // license request rejections
};

const responseBody = (error: StreamingMediaError) => {
  const body = error.responseText ?? error.response ?? error.cause;
  return typeof body === 'string' ? body.slice(0, MAX_TEXT_LENGTH) : undefined;
};

// The browser's own HLS playback reports every load failure as this bare code.
const isNativeHlsLoadFailure = (
  code: number,
  { src, type, usesVhs }: ReturnType<typeof playbackContext>
) =>
  code === PlayerErrorCode.MEDIA_ERR_SRC_NOT_SUPPORTED &&
  !usesVhs &&
  /mpegurl/i.test(type) &&
  /^https?:/.test(src);

/**
 * Reports the error the player currently holds, together with the media it was playing. A load
 * failure of native HLS playback is reported once a probe of the CDN has finished, with its result.
 * Calls made before RUM is initialised are buffered by the SDK and sent once it starts.
 */
export const reportPlayerError = async (player: EnhancedVideoJsPlayer) => {
  const mediaError = player.error() as StreamingMediaError | null;
  const playback = playbackContext(player);
  const media = { ...mediaContext(player), ...playback };

  if (!mediaError) {
    datadogRum.addError(
      namedError('PlayerError', 'Player error event without a media error'),
      { player: media }
    );
    return;
  }

  const { code, message, metadata } = mediaError;
  // VHS misspells the key for timeouts (error-codes.js in @videojs/http-streaming), and
  // contrib-eme for FairPlay license failures (fairplay.js).
  const errorType =
    metadata?.errorType ?? metadata?.erroType ?? metadata?.errortype;
  const error = namedError('PlayerError', `Player error ${code}: ${message}`);
  // Group by type rather than message: VHS messages embed the signed media URL.
  Object.assign(error, {
    dd_fingerprint: `PlayerError ${code} ${errorType ?? message}`,
  });
  const sourceProbe = isNativeHlsLoadFailure(code, playback)
    ? await probeHlsSource(playback.src)
    : undefined;
  datadogRum.addError(error, {
    player: {
      ...media,
      code,
      message,
      errorType,
      requestType: metadata?.requestType,
      requestUri: metadata?.uri,
      keySystem: metadata?.keySystem,
      status: mediaError.status,
      responseBody: responseBody(mediaError),
      cause: metadata?.cause,
      sourceProbe,
    },
  });
};

/** Attaches the media the player last loaded to every later RUM event. */
export const setLastPlayerMediaContext = (player: EnhancedVideoJsPlayer) => {
  datadogRum.setGlobalContextProperty('lastPlayerMedia', {
    mediaIri: player.mediaData?.iri,
    contentType: player.mediaData?.contentType,
    isDrmProtected: player.mediaPlayerData?.source?.isDrmProtected,
  });
};

/** Attaches the DRM systems the browser supports to every later RUM event. */
export const setDrmSupportContext = (
  supportedCDMs: Record<string, boolean>
) => {
  datadogRum.setGlobalContextProperty('drmSupport', supportedCDMs);
};

/**
 * Reports that the server-error page was shown. `errorRef` is the id the server logged the
 * failure under, when the page was rendered from a caught server-side error.
 */
export const reportServerErrorPage = (errorRef: string | undefined) => {
  datadogRum.addError(
    namedError('ServerErrorPage', 'Server error page shown'),
    { serverError: { ref: errorRef } }
  );
};

// A network error out of our control: the browser is offline, or the request was aborted.
// Neither is actionable, so reporting it would just add noise to Datadog.
const isUnreportableNetworkError = (
  networkError: NonNullable<ErrorResponse['networkError']>
): boolean =>
  (typeof window !== 'undefined' && !window.navigator.onLine) ||
  networkError.name === 'AbortError';

/** Reports every GraphQL and network error of a failed Apollo operation. */
export const reportGraphQLErrors = ({
  operation,
  graphQLErrors,
  networkError,
}: ErrorResponse) => {
  const { operationName } = operation;
  // Only this variable: the others can carry user input.
  const mediaIri = operation.variables?.mediaIri;

  graphQLErrors?.forEach(({ message, path, extensions }) => {
    datadogRum.addError(namedError('GraphQLError', message), {
      graphql: { operationName, mediaIri, path, code: extensions?.code },
    });
  });

  if (networkError && !isUnreportableNetworkError(networkError)) {
    datadogRum.addError(networkError, {
      graphql: {
        operationName,
        mediaIri,
        statusCode:
          'statusCode' in networkError ? networkError.statusCode : undefined,
      },
    });
  }
};
